import { formatPhone, type SearchResults } from "@nmt/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { get } from "../api";
import { cx } from "./ui";

interface Hit {
  key: string;
  to: string;
  kind: string;
  title: string;
  sub: string;
}

/** Search box in the top bar: jobs (number, address, claim #) and contacts. "/" focuses it. */
export function SearchBox() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const listId = useId();

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 200);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) && !t.isContentEditable) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => get<SearchResults>(`/api/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2,
    staleTime: 10_000,
  });

  const hits: Hit[] = useMemo(() => {
    const r = results.data;
    if (!r) return [];
    return [
      ...r.jobs.map((j) => ({ key: `j${j.id}`, to: `/jobs/${j.id}`, kind: "Job" as const, title: `#${j.number} ${j.label}`, sub: [j.stageName, j.address].filter(Boolean).join(" · ") })),
      ...r.contacts.map((c) => ({
        key: `c${c.id}`,
        to: `/contacts/${c.id}`,
        kind: c.typeName,
        title: c.displayName,
        sub: [c.company !== c.displayName && c.company, formatPhone(c.phone), c.city].filter(Boolean).join(" · "),
      })),
    ];
  }, [results.data]);

  useEffect(() => setActive(0), [hits]);

  const go = (h: Hit) => {
    navigate(h.to);
    setQ("");
    setOpen(false);
    input.current?.blur();
  };

  const showList = open && debounced.length >= 2;

  return (
    <div className="relative">
      <svg viewBox="0 0 24 24" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-label="Search contacts and jobs"
        placeholder="Search name, phone, address, job #, claim #"
        className="w-full rounded-[var(--radius-control)] border border-line bg-white py-2 pr-3 pl-9 text-[15px] placeholder:text-muted/80 focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, hits.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter" && hits[active]) {
            e.preventDefault();
            go(hits[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {showList && (
        <ul id={listId} role="listbox" className="absolute inset-x-0 top-full z-40 mt-1 max-h-[70vh] overflow-y-auto rounded-[var(--radius-panel)] bg-white py-1 shadow-xl ring-1 ring-line">
          {results.isLoading && <li className="px-4 py-3 text-sm text-muted">Searching…</li>}
          {!results.isLoading && hits.length === 0 && <li className="px-4 py-3 text-sm text-muted">Nothing matches "{debounced}".</li>}
          {hits.map((h, i) => (
            <li
              key={h.key}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                go(h);
              }}
              onMouseEnter={() => setActive(i)}
              className={cx("flex cursor-pointer items-center gap-3 px-4 py-2", i === active && "bg-mist")}
            >
              <span className="w-16 shrink-0 text-xs font-semibold tracking-wide text-muted uppercase">{h.kind}</span>
              <span className="min-w-0">
                <span className="block truncate font-medium">{h.title}</span>
                {h.sub && <span className="block truncate text-sm text-muted">{h.sub}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
